import * as jsPlumb from '@jsplumb/browser-ui';
(window as any).jsPlumb = jsPlumb;

export class JsPlumbEditor {
    public static Instances: { [key: string]: JsPlumbEditor } = {};

    public id: string;
    public dotNetRef: DotNet.DotNetObject;
    public mainElement: HTMLElement;
    public jsPlumbInstance: jsPlumb.BrowserJsPlumbInstance;

    public static InitializeEditor(editorId: string, dotNetRef: DotNet.DotNetObject) {
        JsPlumbEditor.Instances[editorId] = new JsPlumbEditor(editorId, dotNetRef);
    }

    private constructor(editorId: string, dotNetRef: DotNet.DotNetObject) {
        this.id = editorId;
        this.dotNetRef = dotNetRef;

        jsPlumb.ready(() => {
            const mainEl = document.getElementById(editorId);
            if (!mainEl) {
                throw new Error(`Cannot find HTML DOM element with ID ${editorId}`);
            }

            this.mainElement = mainEl;
            this.jsPlumbInstance = jsPlumb.newInstance({
                container: this.mainElement,
                dragOptions: {
                    containment: "parentEnclosed",
                    grid: {w: 16, h: 16},
                }
            });

            this.jsPlumbInstance.addSourceSelector(".flowchart-source-selector", {
                anchor: "Continuous",
            });

            this.jsPlumbInstance.addTargetSelector(".flowchart-node", {
                anchor: "Continuous",
                endpoint: "Rectangle",
            });
        });

        this.jsPlumbInstance.bind("beforeDrop", this.OnBeforeConnectionDrop.bind(this));
        this.jsPlumbInstance.bind("drag:stop", this.OnElementDragStop.bind(this));
    }

    public static AddNode(editorId: string, nodeData: any) { // TODO: Add concrete definition
        const editor = JsPlumbEditor.Instances[editorId];
        if (!editor) {
            throw new Error(`Cannot find JsPlumbEditor instance with ID ${editorId}`);
        }

        editor.AddNodeInternal(nodeData);
    }

    public AddNodeInternal(nodeData: any) { // TODO: Add concrete definition
        const node = document.createElement(nodeData.htmlType);
        node.id = nodeData.id;
        node.innerHTML = nodeData.content.value;
        node.classList.add("flowchart-node");
        for (const c of nodeData.classes) {
            node.classList.add(c);
        }
        node.style.left = `${nodeData.x}px`;
        node.style.top = `${nodeData.y}px`;
        node.addEventListener("dblclick", this.OnNodeDoubleClick.bind(this));

        const sourceSelector = document.createElement("div");
        sourceSelector.classList.add("flowchart-source-selector");
        sourceSelector.title = "Click and drag to create a new connection";
        node.appendChild(sourceSelector);

        this.mainElement.appendChild(node);
        this.jsPlumbInstance.manage(node);
    }

    public static RemoveNode(editorId: string, nodeId: string) {
        const editor = JsPlumbEditor.Instances[editorId];
        if (!editor) {
            throw new Error(`Cannot find JsPlumbEditor instance with ID ${editorId}`);
        }

        editor.RemoveNodeInternal(nodeId);
    }

    public RemoveNodeInternal(nodeId: string) {
        const node = this.mainElement.getElementById(nodeId);
        if (!node) {
            throw new Error(`Cannot find node with ID ${nodeId}`);
        }

        this.jsPlumbInstance.unmanage(node);
        node.remove();
    }

    public static SetFlowchartGraph(editorId: string, graphData: any[]) { // TODO: Add concrete definition
        const editor = JsPlumbEditor.Instances[editorId];
        if (!editor) {
            throw new Error(`Cannot find JsPlumbEditor instance with ID ${editorId}`);
        }

        editor.SetFlowchartGraphInternal(graphData);
    }

    public SetFlowchartGraphInternal(graphData: any[]) { // TODO: Add concrete definition
        if (!Array.isArray(graphData)) {
            throw new Error("Supplied graph data was not an array");
        }

        this.ClearFlowchartInternal();

        for (const node of graphData) {
            this.AddNodeInternal(node);
        }
    }

    public static ClearFlowchart(editorId: string) {
        const editor = JsPlumbEditor.Instances[editorId];
        if (!editor) {
            throw new Error(`Cannot find JsPlumbEditor instance with ID ${editorId}`);
        }

        editor.ClearFlowchartInternal();
    }

    public ClearFlowchartInternal() {
        const nodes = this.mainElement.querySelectorAll(".flowchart-node");

        for (const node of nodes) {
            this.jsPlumbInstance.unmanage(node);
            node.remove();
        }
    }

    public static GetFlowchartData(editorId: string) {
        const editor = JsPlumbEditor.Instances[editorId];
        if (!editor) {
            throw new Error(`Cannot find JsPlumbEditor instance with ID ${editorId}`);
        }

        return editor.GetFlowchartDataInternal();
    }

    public GetFlowchartDataInternal() {
        const ret = { nodes: [], edges: [] };
        const nodes = this.mainElement.querySelectorAll(".flowchart-node");

        for (const node of nodes) {
            // TODO: Determine other required data.
            ret.nodes.push({
                id: node.id,
                // parseFloat strips off the 'px' units at the end.
                x: parseFloat(node.style.left),
                y: parseFloat(node.style.top),
            });
        }

        const connections = this.jsPlumbInstance.select().entries;
        for (const connection of connections) {
            let label = "";
            for (const overlayId in connection.overlays) {
                const overlay = connection.overlays[overlayId];

                if (overlay?.label?.length > 0) {
                    label = overlay.label.trim();
                    break;
                }
            }

            ret.edges.push({
                label,
                source: connection.sourceId,
                target: connection.targetId,
            });
        }

        return ret;
    }

    private OnBeforeConnectionDrop(params: any) {
        let hasLabel = false;
        let hasArrow = false;

        for (const overlayId in params.connection.overlays) {
            const overlay = params.connection.overlays[overlayId];
            const overlayType = overlay?.type.toLowerCase();

            if (overlayType === "label" && overlay.options?.label?.length > 0) {
                hasLabel = true;
            } else if (overlayType === "arrow") {
                hasArrow = true;
            }
        }

        if (!hasLabel) {
            params.connection.addOverlay({ type: "Label", options: { label: "Done", location: 0.5 } }); // TODO: Make configurable and optional based on use case.
        }

        if (!hasArrow) {
            params.connection.addOverlay({ type: "Arrow", options: { location: 1 } });
        }

        return true; // Return false to deny the connection.
    }

    private OnElementDragStop(params: any) {
        const payload = [];
        for (const el of params.elements) {
            payload.push({
                id: el.id,
                x: el.pos.x,
                y: el.pos.y,
            });
        }
        this.dotNetRef.invokeMethodAsync("JS_OnNodesMoved", payload);
    }

    private OnNodeDoubleClick(event: MouseEvent) {
        this.dotNetRef.invokeMethodAsync("JS_OnNodeSelected", event.target.id);
    }
}
